import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Users, 
  Sparkles, 
  Building2, 
  Shield, 
  Code, 
  Search,
  ChevronRight,
  Target,
  Calendar,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const DocumentationPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const documentationSections = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Getting Started",
      color: "indigo",
      articles: [
        { title: "How to create your account", path: "/docs/getting-started/create-account" },
        { title: "Setting up your workspace", path: "/docs/getting-started/workspace-setup" },
        { title: "Inviting your team", path: "/docs/getting-started/invite-team" },
        { title: "Understanding your dashboard", path: "/docs/getting-started/dashboard" }
      ]
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Lead Management",
      color: "blue",
      articles: [
        { title: "Creating and editing leads", path: "/docs/leads/create-edit" },
        { title: "Lead filtering and search", path: "/docs/leads/filtering" },
        { title: "Lead status & pipeline stages", path: "/docs/leads/pipeline-stages" },
        { title: "Lead scoring system", path: "/docs/leads/scoring" }
      ]
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI Features",
      color: "purple",
      articles: [
        { title: "Lead Enrichment", path: "/docs/ai/enrichment" },
        { title: "AI Insight Reports", path: "/docs/ai/insights" },
        { title: "AI Intro Email Generator", path: "/docs/ai/email-generator" },
        { title: "Understanding the Fit Score", path: "/docs/ai/fit-score" }
      ]
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "CRM Modules",
      color: "green",
      articles: [
        { title: "Companies & Contacts", path: "/docs/crm/companies-contacts" },
        { title: "Tasks and Calendar", path: "/docs/crm/tasks-calendar" },
        { title: "Notes and Activity History", path: "/docs/crm/notes-activity" },
        { title: "Email Templates", path: "/docs/crm/email-templates" }
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Admin Panel",
      color: "orange",
      articles: [
        { title: "User management", path: "/docs/admin/user-management" },
        { title: "Role permissions", path: "/docs/admin/permissions" },
        { title: "Usage limits & plan entitlements", path: "/docs/admin/usage-limits" },
        { title: "Company Profile Setup", path: "/docs/admin/company-profile" }
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "SuperAdmin Panel (SaaS Owners)",
      color: "red",
      articles: [
        { title: "Tenant Management", path: "/docs/superadmin/tenant-management" },
        { title: "Plan Upgrades", path: "/docs/superadmin/plan-upgrades" },
        { title: "Stripe Billing", path: "/docs/superadmin/billing" },
        { title: "Platform Analytics", path: "/docs/superadmin/analytics" }
      ]
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Developer & API",
      color: "slate",
      articles: [
        { title: "API usage tracking", path: "/docs/developer/api-tracking" },
        { title: "AI provider configuration", path: "/docs/developer/ai-config" },
        { title: "Webhook integrations", path: "/docs/developer/webhooks" },
        { title: "Authentication & Security", path: "/docs/developer/auth" }
      ]
    }
  ];

  const filteredSections = searchQuery
    ? documentationSections.map(section => ({
        ...section,
        articles: section.articles.filter(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.articles.length > 0)
    : documentationSections;

  const getColorClasses = (color) => {
    const colors = {
      indigo: "bg-indigo-100 text-indigo-600 border-indigo-200",
      blue: "bg-blue-100 text-blue-600 border-blue-200",
      purple: "bg-purple-100 text-purple-600 border-purple-200",
      green: "bg-green-100 text-green-600 border-green-200",
      orange: "bg-orange-100 text-orange-600 border-orange-200",
      red: "bg-red-100 text-red-600 border-red-200",
      slate: "bg-slate-100 text-slate-600 border-slate-200"
    };
    return colors[color] || colors.indigo;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Product Documentation</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Explore guides, tutorials, and best practices to get the most from I-Match.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredSections.map((section, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg border ${getColorClasses(section.color)}`}>
                    {section.icon}
                  </div>
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                </div>
                <CardDescription>
                  {section.articles.length} article{section.articles.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {section.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <button
                        onClick={() => navigate(article.path)}
                        className="w-full text-left flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-100 transition-colors group"
                      >
                        <span className="text-slate-700 group-hover:text-slate-900">
                          {article.title}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {searchQuery && filteredSections.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No results found</h3>
            <p className="text-slate-600">
              Try adjusting your search terms or browse all categories above.
            </p>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12">
          <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="py-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Need help?
                </h3>
                <p className="text-slate-600 mb-6">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/contact')}
                    size="lg"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Contact Support
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.location.href = 'mailto:support@theVinculum.de'}
                  >
                    Email Us
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Contact us at <a href="mailto:support@theVinculum.de" className="text-indigo-600 hover:underline">support@theVinculum.de</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
